/**
 * Coupon shared layer — pure, client-safe types and eligibility helpers.
 *
 * No secrets, no I/O: everything here operates on a CouponPreview the server
 * has already produced (via /api/booking/validate-coupon or peekCoupon on the
 * server). The admin-secret functions live in `@/services/coupon`, which is
 * `server-only`; importing THAT from client code is a build-time error.
 */

/**
 * Peek at coupon info — used on search results / property pages where the
 * guest hasn't picked a room or entered an email yet.
 *
 * Returns ENOUGH information for the UI to decide which properties + room
 * types should display the discount banner without lying about scope or
 * showing a price the checkout will refuse to honour.
 */
export interface CouponPreview {
  valid: boolean
  /** The code itself, uppercased, for use in banner messages */
  code?: string
  /** Reason the coupon is invalid — populated only when valid === false */
  reason?: 'not_found' | 'inactive' | 'not_yet' | 'expired' | 'usage_exhausted'
  discountType?: string
  discountValue?: number
  currency?: string | null
  minNights?: number | null
  minAmount?: number | null
  /** Booking-window (ISO YYYY-MM-DD) — used to message "expired on…" when relevant */
  validFrom?: string
  validUntil?: string | null
  /** Stay-window (ISO YYYY-MM-DD) — caller compares against check-in/out */
  stayFrom?: string | null
  stayUntil?: string | null
  /** When property_scope_mode === 'restricted', UI must hide on properties NOT in this set */
  scopeMode?: 'all' | 'restricted'
  allowedPropertyIds?: string[]
  /** Names for restricted properties — UI lists them in the "valid only at X, Y" message */
  allowedPropertyNames?: Array<{ id: string; name_en: string; name_ar: string | null }>
  /** Per-property discount overrides (also lists every property if scope=restricted) */
  propertyOverrides?: Array<{ propertyId: string; discountValue: number }>
  /** Room-type restrictions keyed by property — empty list under a key = all room types */
  roomTypesByProperty?: Record<string, string[]>
}


/**
 * Stay-window check: the booking's stay must overlap [stay_from, stay_until].
 * We require the entire stay (check-in through check-out) to fall within the
 * window so a Hajj-only promo can't subsidise a partial-overlap booking.
 * (Pure duplicate of the server-only implementation in `@/services/coupon` —
 * this module must stay importable from client code.)
 */
function isWithinStayWindow(
  stayFrom: string | null,
  stayUntil: string | null,
  checkIn?: string,
  checkOut?: string,
): boolean {
  if (!stayFrom && !stayUntil) return true
  if (!checkIn || !checkOut) return true // caller didn't pass dates — skip
  if (stayFrom && checkIn < stayFrom) return false
  // checkOut is the morning of departure; the last NIGHT is checkOut - 1 day.
  // Use checkOut <= stay_until + 1 day so a stay through the last allowed night passes.
  if (stayUntil) {
    const lastAllowed = new Date(stayUntil)
    lastAllowed.setUTCDate(lastAllowed.getUTCDate() + 1)
    const lastIso = lastAllowed.toISOString().slice(0, 10)
    if (checkOut > lastIso) return false
  }
  return true
}

/**
 * Helpers for callers that already hold a CouponPreview and need to ask
 * "does this coupon apply to property X / room type Y / dates D?".
 */
export function couponAppliesToProperty(preview: CouponPreview, propertyId: string): boolean {
  if (!preview.valid) return false
  if (preview.scopeMode === 'restricted') {
    return preview.allowedPropertyIds?.includes(propertyId) ?? false
  }
  return true
}

export function couponAppliesToRoomType(
  preview: CouponPreview,
  propertyId: string,
  roomTypeId: string,
): boolean {
  if (!couponAppliesToProperty(preview, propertyId)) return false
  const allowed = preview.roomTypesByProperty?.[propertyId]
  if (!allowed || allowed.length === 0) return true // no room-type restriction for this property
  return allowed.includes(roomTypeId)
}

export function couponAppliesToStay(
  preview: CouponPreview,
  checkIn?: string,
  checkOut?: string,
): boolean {
  if (!preview.valid) return false
  return isWithinStayWindow(preview.stayFrom ?? null, preview.stayUntil ?? null, checkIn, checkOut)
}

export function couponMeetsMinNights(preview: CouponPreview, nights: number): boolean {
  if (!preview.valid) return false
  if (!preview.minNights) return true
  return nights >= preview.minNights
}

/**
 * Resolve the effective per-property discount value the UI should display.
 * Returns null when the coupon does NOT apply to this property, so the caller
 * can skip rendering the strikethrough/discount price.
 */
export function resolvePropertyDiscount(
  preview: CouponPreview,
  propertyId: string,
): { discountType: string; discountValue: number } | null {
  if (!couponAppliesToProperty(preview, propertyId)) return null
  if (!preview.discountType || preview.discountValue == null) return null
  const override = preview.propertyOverrides?.find(o => o.propertyId === propertyId)
  return {
    discountType: preview.discountType,
    discountValue: override?.discountValue ?? preview.discountValue,
  }
}

/**
 * Discriminated reason for why a coupon doesn't apply to the user's
 * current search / property context. Each variant carries the params the
 * UI needs to render the specific banner ("HAJJ20 only valid Jun 24-30").
 *
 * `eligible: true` is the happy path — banner shows the active discount.
 * `eligible: false` with `reason='generic'` is the fallback when the
 * preview is invalid for an unspecified reason (e.g. unknown code).
 */
export type CouponEligibility =
  | { eligible: true }
  | { eligible: false; reason: 'not_found'; code?: string }
  | { eligible: false; reason: 'expired'; code: string; validUntil: string }
  | { eligible: false; reason: 'not_yet'; code: string; validFrom: string }
  | { eligible: false; reason: 'usage_exhausted'; code: string }
  | { eligible: false; reason: 'stay_window'; code: string; stayFrom?: string | null; stayUntil?: string | null }
  | { eligible: false; reason: 'min_nights'; code: string; minNights: number }
  | { eligible: false; reason: 'min_amount'; code: string; minAmount: number; currency: string }
  | { eligible: false; reason: 'property_scope'; code: string; allowedProperties: Array<{ id: string; name_en: string; name_ar: string | null }> }
  | { eligible: false; reason: 'generic'; code?: string }

/**
 * Decide whether a coupon preview applies to the search/property context,
 * AND if not, why not — so the UI can show "HAJJ20 only valid for Hajj
 * stays" instead of a generic "doesn't apply" dead-end.
 *
 * Order is intentional: the most actionable reasons come first so guests
 * see "extend your stay" / "try different dates" before "wrong property"
 * (which often means abandoning the search entirely).
 */
export function evaluateCouponEligibility(
  preview: CouponPreview,
  ctx: {
    /** Restrict to a specific property (property detail page, BookingSidebar). */
    propertyId?: string
    /** Stay dates for stay-window + min-nights checks. */
    checkIn?: string
    checkOut?: string
    nights?: number
    /** All properties currently listed in the search results. Used by the
     *  search page to ask "does ANY visible property satisfy the scope?" */
    visiblePropertyIds?: string[]
  },
): CouponEligibility {
  if (!preview.valid) {
    if (preview.reason === 'expired' && preview.code && preview.validUntil) {
      return { eligible: false, reason: 'expired', code: preview.code, validUntil: preview.validUntil }
    }
    if (preview.reason === 'not_yet' && preview.code && preview.validFrom) {
      return { eligible: false, reason: 'not_yet', code: preview.code, validFrom: preview.validFrom }
    }
    if (preview.reason === 'usage_exhausted' && preview.code) {
      return { eligible: false, reason: 'usage_exhausted', code: preview.code }
    }
    return { eligible: false, reason: 'not_found', code: preview.code }
  }

  const code = preview.code || ''

  // Stay-window first — most common Hajj/Eid promo mismatch
  if (!isWithinStayWindow(preview.stayFrom ?? null, preview.stayUntil ?? null, ctx.checkIn, ctx.checkOut)) {
    return {
      eligible: false,
      reason: 'stay_window',
      code,
      stayFrom: preview.stayFrom,
      stayUntil: preview.stayUntil,
    }
  }

  // Min nights
  if (ctx.nights != null && preview.minNights && ctx.nights < preview.minNights) {
    return { eligible: false, reason: 'min_nights', code, minNights: preview.minNights }
  }

  // Property scope
  if (preview.scopeMode === 'restricted') {
    const allowed = preview.allowedPropertyIds || []
    if (ctx.propertyId) {
      // Targeted: viewing one property — does the coupon include it?
      if (!allowed.includes(ctx.propertyId)) {
        return {
          eligible: false,
          reason: 'property_scope',
          code,
          allowedProperties: preview.allowedPropertyNames || [],
        }
      }
    } else if (ctx.visiblePropertyIds && ctx.visiblePropertyIds.length > 0) {
      // Search list: does ANY visible property qualify?
      const overlap = ctx.visiblePropertyIds.some(id => allowed.includes(id))
      if (!overlap) {
        return {
          eligible: false,
          reason: 'property_scope',
          code,
          allowedProperties: preview.allowedPropertyNames || [],
        }
      }
    }
  }

  return { eligible: true }
}
